import React, { createContext, useContext, useEffect, useState } from 'react';
import { io, Socket } from 'socket.io-client';

export interface FlyBrainState {
  dopamine: number;
  spikeRate: number;
  topActive: number[];
  synapseCount: number;
  globalTime: number;
  watchedTokens: number;
  openPositions: number;
  motorDecisions: Array<{ type: string; ts: number; strength: number; msg: string }>;
  connected: boolean;
}

interface SocketContextState {
  socket: Socket | null;
  connected: boolean;
  trades: any[];
  serverOpenPositions: any[];
  serverMomentumWatchlist: any[];
  alphaRadarList: any[];
  flyBrainState: FlyBrainState | null;
  serverPaperStats: any | null;
}

const SocketContext = createContext<SocketContextState>({
  socket: null,
  connected: false,
  trades: [],
  serverOpenPositions: [],
  serverMomentumWatchlist: [],
  alphaRadarList: [],
  flyBrainState: null,
  serverPaperStats: null,
});

export const useSocket = () => useContext(SocketContext);

export function SocketProvider({ children }: { children: React.ReactNode }) {
  const [socket, setSocket] = useState<Socket | null>(null);
  const [connected, setConnected] = useState(false);
  const [trades, setTrades] = useState<any[]>([]);
  const [serverOpenPositions, setServerOpenPositions] = useState<any[]>([]);
  const [serverMomentumWatchlist, setServerMomentumWatchlist] = useState<any[]>([]);
  const [alphaRadarList, setAlphaRadarList] = useState<any[]>([]);
  const [flyBrainState, setFlyBrainState] = useState<FlyBrainState | null>(null);
  const [serverPaperStats, setServerPaperStats] = useState<any | null>(null);

  useEffect(() => {
    // Initial fetch to populate state before the first socket broadcast
    fetch('/api/server-paper-open-positions')
      .then(res => res.json())
      .then(data => { if (Array.isArray(data)) setServerOpenPositions(data); })
      .catch(() => {});

    fetch('/api/server-paper-momentum-watchlist')
      .then(res => res.json())
      .then(data => { if (Array.isArray(data)) setServerMomentumWatchlist(data); })
      .catch(() => {});

    fetch('/api/server-paper-stats')
      .then(res => res.json())
      .then(data => { if (data && !data.error) setServerPaperStats(data); })
      .catch(() => {});

    const socketInstance = io('/', {
      transports: ['websocket', 'polling'],
    });

    socketInstance.on('connect', () => {
      console.log('[Socket.IO] Connected to server');
      setConnected(true);
    });

    socketInstance.on('disconnect', () => {
      console.log('[Socket.IO] Disconnected from server');
      setConnected(false);
    });

    socketInstance.on('graphql-subscription-data', (data) => {
      console.log('[Socket.IO] Received trade update:', data);
      if (data.data?.tradeSubscribed) {
        setTrades(prev => [data.data.tradeSubscribed, ...prev].slice(0, 100));
      }
    });

    socketInstance.on('server-paper-open-positions', (data) => {
      if (Array.isArray(data)) {
        setServerOpenPositions(data);
      }
    });

    socketInstance.on('server-paper-momentum-watchlist', (data) => {
      if (Array.isArray(data)) {
        setServerMomentumWatchlist(data);
      }
    });

    socketInstance.on('server-alpha-radar', (data) => {
      if (Array.isArray(data)) {
        setAlphaRadarList(data);
      }
    });

    socketInstance.on('server-paper-stats', (data) => {
      if (data && !data.error) {
        setServerPaperStats(data);
      }
    });

    socketInstance.on('fly_brain_state', (data: FlyBrainState) => {
      setFlyBrainState(data);
    });

    // Subscribe to trades feed
    socketInstance.emit('graphql-subscription', { query: 'trades' });

    setSocket(socketInstance);

    return () => {
      socketInstance.disconnect();
    };
  }, []);

  return (
    <SocketContext.Provider value={{ socket, connected, trades, serverOpenPositions, serverMomentumWatchlist, alphaRadarList, flyBrainState, serverPaperStats }}>
      {children}
    </SocketContext.Provider>
  );
}
